import type { FeedbackType } from '@bandos/shared'
import { DismissibleTooltip } from '../../../../common/DismissibleTooltip'
import { StakeholderAvatar } from '../../../../../features/stakeholder'
import {
  RatingItem,
  RatingHeader,
  RatingName,
  RatingRole,
  RatingScore,
  StructuredFeedback,
} from '../styles/StakeholderRatingsFooterStyles'
import { RaterInfoWrapper, ClickableAvatar, RaterDetails } from '../styles/RaterCardStyles'
import { FeedbackItem } from './FeedbackItem'
import type { RaterCardProps } from '../types'

/**
 * A card showing a single stakeholder's feedback.
 */
export function RaterCard({
  rater,
  roleLabel,
  categoryStyle,
  onOpenChat,
  hasChatContext,
  portraitUrl,
  onAvatarClick,
  avatarTooltip = 'View profile',
}: RaterCardProps): React.JSX.Element {
  const handleChat = hasChatContext
    ? (feedbackType: FeedbackType, content: string) => onOpenChat(feedbackType, content)
    : undefined

  const avatarElement = <StakeholderAvatar name={rater.name} portraitUrl={portraitUrl} size="sm" />

  return (
    <RatingItem>
      <RatingHeader>
        <RaterInfoWrapper>
          {onAvatarClick ? (
            <DismissibleTooltip title={avatarTooltip} placement="top" mouseEnterDelay={0.3}>
              <ClickableAvatar
                onClick={(e) => {
                  e.stopPropagation()
                  onAvatarClick()
                }}
              >
                {avatarElement}
              </ClickableAvatar>
            </DismissibleTooltip>
          ) : (
            avatarElement
          )}
          <RaterDetails>
            <RatingName>{rater.name}</RatingName>
            <RatingRole>{roleLabel}</RatingRole>
          </RaterDetails>
        </RaterInfoWrapper>
        <RatingScore $accentColor={categoryStyle.accentColor}>{rater.rating}/10</RatingScore>
      </RatingHeader>
      <StructuredFeedback>
        <FeedbackItem
          label="Concerns"
          content={rater.mainRisks}
          tooltipText="Discuss this concern"
          accentColor={categoryStyle.accentColor}
          iconColor={categoryStyle.iconColor}
          onChat={handleChat ? () => handleChat('concerns', rater.mainRisks) : undefined}
        />
        <FeedbackItem
          label="Suggestions"
          content={rater.suggestions}
          tooltipText="Discuss this suggestion"
          accentColor={categoryStyle.accentColor}
          iconColor={categoryStyle.iconColor}
          onChat={handleChat ? () => handleChat('suggestions', rater.suggestions) : undefined}
        />
        <FeedbackItem
          label="Would ask"
          content={rater.question}
          tooltipText="Answer this question"
          accentColor={categoryStyle.accentColor}
          iconColor={categoryStyle.iconColor}
          onChat={handleChat ? () => handleChat('question', rater.question) : undefined}
        />
      </StructuredFeedback>
    </RatingItem>
  )
}
